import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Switch, Table } from 'antd';
import type { TableProps } from 'antd';
import RoomConfigData, { HotelsConfigData, HotelsConfigEditData, HotelsConfigEditStatusData, ServiceDto } from '../../../../../../models/MappingData';
import { DeleteOutlined, EditOutlined, ImportOutlined, KeyOutlined, StarFilled, StarTwoTone } from '@ant-design/icons';
import { Row, Col, Button, Drawer } from 'antd';
import { deleteHotelsConfigMapping, editHotelsStatusConfig, fetchHotelsConfigMapping, fetchLanguagesAvailables, ForceRefreshCache, getAllServices } from '../../../../../../api/repositoryEP';
import { notification } from 'antd';
import './HotelsConfig.scss';
import ModalGeneric from '../Modals/ModalGeneric';
import HotelForm from '../Forms/HotelsForm/HotelsForm';
import Search from 'antd/es/input/Search';
import { GetKeyIcon } from '../../../../../../utils/icons';
import { GetStarIcon } from '../../../../../../utils/icons';
import { PageSize } from '../../../../../../utils/constants';
import { CustomPagination } from '../../../../..';
import LoadingDataComponent from '../../../../../common/LoadingData/LoadingDataComponent';
import { GenericModal } from '../../../../../common/Modal/GenericModal';


interface HotelsConfigProps {
    language: string;
}

const HotelsConfig = (props: HotelsConfigProps): JSX.Element => {

    const { t, i18n } = useTranslation();
    const [data, setData] = useState<HotelsConfigData[]>([]);
    const drawerWidth = window.innerWidth <= 768 ? '100%' : '40%';
    const [newHotelDrawer, setNewHotelDrawer] = useState<boolean>(false);
    const [editHotelDrawer, setEditHotelDrawer] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [hotelData, setHotelData] = useState<HotelsConfigData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const [actualPage, setActualPage] = useState<number>(1);
    const [actualPageSize, setActualPageSize] = useState<number>(PageSize);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [filter, setFilter] = useState<string>('');

    const onSearch = (value: string) => {
        setFilter(value);
        setActualPage(1); // 👈 siempre empezamos en la primera página al buscar
    };

    const [isForcingCache, setIsForcingCache] = useState<boolean>(false);
    const onForceRefreshCache = (hotelIntegrationIds: string[], language: string) => {
        setIsForcingCache(true);
        ForceRefreshCache(hotelIntegrationIds, language).then((response) => {
            notification.success({
                message: t("components.tab-mapping.cache-refreshed.title"),
                description: t("components.tab-mapping.cache-refreshed.description"),
                placement: 'topRight',
            });
        }).finally(() => {
            setIsForcingCache(false);
        });
    }

    const [availableWALanguages, setAvailableWALanguages] = useState<string[]>([]);
    const getAvailableWALanguages = () => {
        fetchLanguagesAvailables("wa").then((r) => {
            if (r && r.data) {
                setAvailableWALanguages(r.data);
            } else {
                setAvailableWALanguages([]);
            }
        });
    }


    const columns: TableProps<HotelsConfigData>['columns'] = [
        {
            title: t("components.tab-mapping.hotels-config.table.c1"),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t("components.tab-mapping.hotels-config.table.c2"),
            key: 'code',
            render: (obj) => (
                <div>
                    {obj.code} / {obj.integrationCode}
                </div>
            ),
        },
        {
            title: t("components.tab-mapping.hotels-config.table.c3"),
            key: 'locationCode',
            render: (obj) => (
                <div>
                    {obj.locationCode} / {obj.locationIntegrationCode}
                </div>
            ),
        },
        {
            title: t("components.tab-mapping.hotels-config.table.c4"),
            key: 'category',
            render: (obj) => (
                <div className='icon-hotelTable-container'>
                    {obj.category.number} {obj.category.type === 'star' ? GetStarIcon("app-icon width-18px") : GetKeyIcon("app-icon width-18px")}
                </div>
            ),
        },
        {
            title: t("components.tab-mapping.hotels-config.table.c5"),
            dataIndex: 'destination',
            key: 'destination',
        },
        {
            title: t("components.tab-mapping.hotels-config.table.c6"),
            dataIndex: 'numberRooms',
            key: 'numberRooms',
        },
        {
            title: t("components.tab-mapping.hotels-config.table.c7"),
            key: 'action',
            render: (obj) => (
                <div className='app-icon-table-container'>
                    <EditOutlined className='app-icon-table' onClick={() => editHotel(obj)} />
                    <DeleteOutlined className='app-icon-table' onClick={() => openDeleteModal(obj)} />

                    <Switch
                        size='small'
                        checked={obj.active}
                        style={{ marginRight: 5 }}
                        onChange={(checked) => changeStatus(obj, checked)}
                    />
                </div>
            ),
        },
    ];
    const onChangePagination = (newPage: number, newPageSize?: number | undefined) => {
        setActualPage(newPage);
        setActualPageSize(newPageSize ?? PageSize);
    }


    useEffect(() => {
        if (!newHotelDrawer && !editHotelDrawer && !deleteModalVisible) {
            setLoading(true);
            fetchHotelsConfigMapping(props.language, filter, actualPage, actualPageSize).then((response) => {
                if (response?.type === 1) {
                    setData(response.data.elements); // 👈 asegurate que el backend devuelve { elements, totalRecords }
                    setTotalRecords(response?.data.totalRecords as number);
                }
                setLoading(false);
            });
        }
    }, [newHotelDrawer, editHotelDrawer, deleteModalVisible, props.language, filter, actualPage, actualPageSize]);



    const editHotel = (hotel: HotelsConfigData) => {
        setHotelData(hotel);
        setEditHotelDrawer(true)
    }

    const deleteHotel = async () => {
        if (hotelData?.id) {
            setDeletingHotel(true);
            deleteHotelsConfigMapping(hotelData.id).then((r) => {
                if (r?.type === 1) {
                    setDeletingHotel(false);
                    setDeleteModalVisible(false);
                    setHotelData(null);
                } else {
                    setDeletingHotel(false);

                }
            });
        }
    };
    const cancelDeleteHotel = () => {
        setDeleteModalVisible(false);
        setHotelData(null);
    }
    const changeStatus = (hotel: HotelsConfigData, newActiveStatus: boolean) => {
        const h: HotelsConfigEditStatusData = {
            id: hotel.id,
            active: newActiveStatus
        };

        editHotelsStatusConfig(h).then((response) => {
            // Actualizar el estado global para reflejar el cambio en la UI
            setData(prevData => prevData.map(h =>
                h.id === hotel.id ? { ...h, active: newActiveStatus } : h
            ));
        });
    };




    const handleAddButton = () => {
        setHotelData(null)
        setNewHotelDrawer(true)
    }

    useEffect(() => {
        getAvailableWALanguages();
    }, [])




    const [servicesAvailable, setServicesAvailable] = useState<ServiceDto[]>([]);
    const [loadingServices, setLoadingServices] = useState<boolean>(false);

    useEffect(() => {
        if (newHotelDrawer || editHotelDrawer) {
            setLoadingServices(true);
            getAllServices(i18n.language).then((response) => {
                if (response?.type === 1) {
                    setServicesAvailable(response.data as ServiceDto[]);
                }
            }).finally(() => {
                setLoadingServices(false);
            });
        }

    }, [newHotelDrawer, editHotelDrawer]);


    const [deletingHotel, setDeletingHotel] = useState<boolean>(false);

    const openDeleteModal = (hotel: HotelsConfigData) => {
        setHotelData(hotel);
        setDeleteModalVisible(true);
    }


    return (
        <div className="hotels-configuration-component">


            {deleteModalVisible && hotelData?.id &&
                <GenericModal
                    isLoading={deletingHotel}
                    title={t("components.tab-mapping.room-config.modal-title")}
                    closable
                    content={<div>{t("components.tab-mapping.room-config.modal-msg")}</div>}
                    visible={deleteModalVisible}
                    onOk={deleteHotel}
                    onCancel={cancelDeleteHotel}
                />
            }
            <Drawer
                open={editHotelDrawer}
                title={t("components.tab-mapping.hotels-config.drawer.title-edit")}
                onClose={() => {
                    setEditHotelDrawer(false);
                    setHotelData(null)
                }}
                width={drawerWidth}
                closable>
                {loadingServices ?
                    (<LoadingDataComponent />)
                    :
                    (<HotelForm
                        setDrawerVisible={setEditHotelDrawer}
                        drawerVisible={editHotelDrawer}
                        availableWALanguages={availableWALanguages}
                        hotelData={hotelData}
                        setHotelData={setHotelData}
                        language={props.language}
                        isForcingCache={isForcingCache}
                        onForceRefreshCache={onForceRefreshCache}
                        servicesAvailable={servicesAvailable}
                    />)

                }

            </Drawer>

            <Drawer
                open={newHotelDrawer}
                title={t("components.tab-mapping.hotels-config.drawer.title-new")}
                onClose={() => {
                    setNewHotelDrawer(false);
                    setHotelData(null)
                }}
                width={drawerWidth}
                closable>
                {loadingServices ?
                    (<LoadingDataComponent />)
                    :
                    (<HotelForm
                        setDrawerVisible={setNewHotelDrawer}
                        availableWALanguages={availableWALanguages}
                        drawerVisible={newHotelDrawer}
                        hotelData={null}
                        setHotelData={setHotelData}
                        language={props.language}
                        isForcingCache={isForcingCache}
                        onForceRefreshCache={onForceRefreshCache}
                        servicesAvailable={servicesAvailable}
                    />)
                }


            </Drawer>

            <Row gutter={[10, 10]} style={{ flexDirection: 'column' }}>
                <Col xs={24} md={24}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={24} style={{ fontFamily: 'Montserrat' }}>
                            {t("components.tab-mapping.hotels-config.msg-info")}
                        </Col>
                        <Col xs={24} md={24} lg={16}>
                            <Search
                                placeholder={t("components.tab-mapping.hotels-config.search") || ''}
                                allowClear
                                onSearch={onSearch}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={4} className='button-container'>
                            <Button
                                className='app-button'
                                onClick={() => handleAddButton()}>
                                {t("components.tab-mapping.hotels-config.button-add")}
                            </Button>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={4} className='button-container'>
                            <Button disabled className='app-button'><ImportOutlined /> {t("components.tab-mapping.room-config.button-import")}</Button>
                        </Col>
                    </Row>
                </Col>
                <Col xs={24} md={24}>
                    <Table
                        className='table'
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                    />
                </Col>

            </Row>
            {totalRecords > 0 && (
                <div className="hotels-pagination-footer">
                    <span className="hotels-pagination-footer__count">
                        {t('components.tab-mapping.hotels-config.total', { count: totalRecords })}
                    </span>
                    <CustomPagination
                        actualPage={actualPage}
                        actualPageSize={actualPageSize}
                        totalElements={totalRecords}
                        onChange={onChangePagination}
                    />
                </div>
            )}
        </div>
    );
};



export default HotelsConfig;